import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { FilterDefinition, ListViewColumn } from './list-view';
import { capitalize } from '../../../services/format';
import { uniqueList } from '../../../services/form-helpers';
import { generateRandomString } from '../../../services/misc';

export const convertFilterStateToQuery = (filterState: any, filters: FilterDefinition[], columns: ListViewColumn[], globalText: string) => {

}

export const convertFilterDefinitionToString = (filterState: any) => {

}
export enum SortDirection {
  Asc,
  Desc
}
export interface SortDefinition {
  direction: SortDirection,
  path: string
}

export enum LogicalOperator {
  Equal,
  NotEqual,
  Like
}
export enum LogicalConnector {
  And,
  Or
}
export interface QueryFilter {
  accessor?: string,
  value?: any,
  logicalOperator?: LogicalOperator,
  logicalConnector?: LogicalConnector,
  filters?: QueryFilter[],
  stringExpression?: boolean
}

export interface QueryDefinition {
  filters?: QueryFilter[],
  page: number,
  limit: number,
  sortDefinition?: SortDefinition,
  pKs?: any[],
  primaryKeyColumn?: string
}

export type DataSourceSelector = (state: any) => any;

export interface DataSourceRequest {
  name: string;
  request: (parameters?: any) => Promise<any>;
  appendData?: boolean;
}

export interface DataSource {
  name: string,
  pk: string,
  additionalSelector?: DataSourceSelector,
  requestArgs?: (state, watch?: any, entity?: any) => any,
  // Form fields (react-hook-form) this list should be scoped by when it is
  // embedded in a form. The grid subscribes to them via the form `watch` and
  // re-fetches when they change (see FrameWorkListView).
  watchFields?: string[],
  request?(parameters?: any): Promise<any>,
  entityRequest?(parameters?: any): Promise<any>,
  entityRequestArgs?: ({ getValues, additionaSelector }) => any,
  createRequest?(parameters?: any): Promise<any>,
  updateRequest?(parameters?: any): Promise<any>,
  skipLocalUpdate?: boolean,
  deleteRequest?(parameters?: any): Promise<any>,
  additionalRequests?: DataSourceRequest[],
  selector?: DataSourceSelector,
  serverSideEvents?: boolean,

  //only used for attachments currently.
  entityName?: string,
  entityTypeId?: any,

  //useful when the model in the backend needs to be converted to a different structure for the front-end
  //Note convertToViewModel will execute on the current entity.
  //Note convertToDataModel will execute right before form submission for insert and update requests
  convertToViewModel?: (dataModel: any) => any,
  convertToDataModel?: (viewModel: any) => any,
}

/*
base level definition - repository: { name: 'dataSourceName', actions: { setCurrent: null }, dataSource: {} } as any
*/
export interface Repository {
  fetchData: any,
  createAction: any,
  updateAction: any,
  deleteAction: any,
  reducer: any,
  dataSource: DataSource,
  actions: any,
  additionalActions: any
}

export const createReduxStore = (dataSource: DataSource) => {
  const { name: key, request, selector, createRequest, updateRequest, deleteRequest, pk, serverSideEvents, additionalRequests, skipLocalUpdate } = dataSource;

  const reqs = additionalRequests?.reduce((obj, current) => {
    obj[current.name] = {
      status: 'idle',
      error: null,
    }
    return obj;
  }, {});

  let initialState = {
    status: 'idle',
    error: null,
    data: [],
    total: 0,
    payloadId: null,
    latestRequestId: null,
    current: {
      data: null,
      status: 'idle',
      error: null,
    },
    createAction: {
      status: 'idle',
      error: null,
      createdEntity: null
    },
    updateAction: {
      status: 'idle',
      error: null,
    },
    deleteAction: {
      status: 'idle',
      error: null,
    },
    ...reqs
  } as any;
  initialState[key] = [];

  const additionalThunks = additionalRequests?.reduce((obj, current) => {
    obj[current.name] = createAsyncThunk(key + current.name, async (...args: any[]) => {
      const response = await current.request(...args);
      return response;
    });
    return obj;
  }, {});

  const fetchData = createAsyncThunk(key, async (...args: any[]) => {
    if (request) {
      let argument = [];
      const p = args && args?.length ? args[0] : null;
      if (p && p instanceof Array && p.length) {
        argument = p;
      } else if (p && p instanceof Object) {
        argument = [p];
      } else if (p != null) {
        argument = [p];
      }
      const response = await request(...argument);
      return response;
    } else {
      //no request provided. load data from selector
      return selector;
    }

  });

  const createAction = createAsyncThunk(key + '1', async (...args1: any[]) => {
    const response = await createRequest(...args1);
    return response;
  });

  const updateAction = createAsyncThunk(key + '2', async (...args2: any[]) => {
    const response = await updateRequest(...args2);
    return response;
  });

  const deleteAction = createAsyncThunk(key + '3', async (...args3: any[]) => {
    const response = await deleteRequest(...args3);
    return response;
  });

  const statusActions = additionalRequests?.reduce((obj, current) => {
    obj['set' + capitalize(current.name) + 'Status'] = (state, action) => {
      state[current.name].status = action.payload;
    };
    return obj;
  }, {});

  const dataSlice = createSlice({
    name: key,
    initialState,
    reducers: {
      setData: (state, action) => {
        state[key] = action.payload;
      },
      setCurrent: (state, action) => {
        state.current.data = action.payload;
      },
      setCreateStatus: (state, action) => {
        state.createAction.status = action.payload;
      },
      setUpdateStatus: (state, action) => {
        state.updateAction.status = action.payload;
      },
      setDeleteStatus: (state, action) => {
        state.deleteAction.status = action.payload;
      },
      // Client-side grids: rows remaining after table filters + search.
      setFilteredTotal: (state: any, action) => {
        state.filteredTotal = action.payload;
      },
      ...statusActions
    },
    extraReducers(builder) {
      builder
        .addCase(fetchData.pending, (state, action) => {
          state.status = 'loading';
          // Track the most recently dispatched fetch so out-of-order responses can be discarded.
          state.latestRequestId = action.meta.requestId;
        })
        .addCase(fetchData.fulfilled, (state, action) => {
          // Ignore stale responses: only apply the result of the latest dispatched fetch.
          if (state.latestRequestId !== action.meta.requestId) {
            return;
          }
          if (!(action.payload instanceof Function)) {
            state.status = 'succeeded';
            if (serverSideEvents) {
              state.data = action.payload.result;
              state.total = action.payload.total;
            } else {
              state.data = action.payload; //conversion here
            }
            state.payloadId = generateRandomString(10);
          } else {
            //our payload is a selector! useful when computing a data source from other data sources
            state.status = 'succeeded';
            state.data = action.payload(state);
          }
        })
        .addCase(fetchData.rejected, (state: any, action: any) => {
          // Ignore stale rejections from superseded fetches.
          if (state.latestRequestId !== action.meta.requestId) {
            return;
          }
          state.status = 'failed';
          state.error = action.error.message;
        });

      builder
        .addCase(createAction.pending, (state, action) => {
          state.createAction.status = 'loading';
        })
        .addCase(createAction.fulfilled, (state, action) => {
          state.createAction.status = 'succeeded';
          state.data = [
            ...state.data,
            action.payload,
          ];
          state.createAction.createdEntity = action.payload;
          state.createAction.error = null;
        })
        .addCase(createAction.rejected, (state: any, action: any) => {
          state.createAction.status = 'failed';
          state.createAction.error = action.error.message;
        });

      builder
        .addCase(updateAction.pending, (state, action) => {
          state.updateAction.status = 'loading';
        })
        .addCase(updateAction.fulfilled, (state, action) => {
          state.updateAction.status = 'succeeded';
          if (!skipLocalUpdate) {
            state.data = [
              ...state.data.filter(d => d[pk] != action.payload[pk]),
              action.payload,
            ];
            state.current.data = action.payload;
          }
          state.updateAction.error = null;
        })
        .addCase(updateAction.rejected, (state: any, action: any) => {
          state.updateAction.status = 'failed';
          state.updateAction.error = action.error.message;
        });

      builder
        .addCase(deleteAction.pending, (state, action) => {
          state.deleteAction.status = 'loading';
        })
        .addCase(deleteAction.fulfilled, (state, action) => {
          state.deleteAction.status = 'succeeded';
          state.data = [
            ...state.data.filter(d => d[pk] != action.payload[pk]),
            action.payload,
          ];
          state.deleteAction.error = null;
        })
        .addCase(deleteAction.rejected, (state: any, action: any) => {
          state.deleteAction.status = 'failed';
          state.deleteAction.error = action.error.message;
        });

      additionalRequests?.forEach(additionalRequest => {
        const additionalAction = additionalThunks[additionalRequest.name];
        const name = additionalRequest.name;
        const appendData = additionalRequest.appendData;
        builder
          .addCase(additionalAction.pending, (state, action) => {
            state[name].status = 'loading';
          })
          .addCase(additionalAction.fulfilled, (state, action) => {
            state[name].status = 'succeeded';
            state[name].data = action.payload;
            state[name].error = null;
          })
          .addCase(additionalAction.rejected, (state: any, action: any) => {
            state[name].status = 'failed';
            state[name].error = action.error.message;
          });

      })
    },
  });

  const {
    setCurrent, setCreateStatus, setUpdateStatus, setDeleteStatus, setFilteredTotal, ...additionalActions
  } = dataSlice.actions;

  return { additionalActions: { ...additionalActions, ...additionalThunks } as any, fetchData, createAction, deleteAction, updateAction, reducer: dataSlice.reducer, dataSource, actions: { setCurrent, setCreateStatus, setUpdateStatus, setDeleteStatus, setFilteredTotal } };
}

// Builds a data source's redux store AND registers its reducer with the root
// reducer (the self-registration pattern the views use), so callers don't repeat
// the createReduxStore + reduxReducer[name] + setReduxReducer boilerplate.
// Returns the full Repository (fetchData, actions, reducer, …) for reference.
// The store module is required lazily (not imported at the top) because store.ts
// calls createReduxStore during its own module init — a top-level import here
// would form a load-time cycle and run store before createReduxStore is defined.
export const registerDataSource = (dataSource: DataSource): Repository => {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { reduxReducer, setReduxReducer } = require('../../../store/store');
  const repository = createReduxStore(dataSource);
  reduxReducer[dataSource.name] = repository.reducer;
  setReduxReducer(reduxReducer);
  return repository;
}

